/**
 * IonSurface — the single always-mounted right-anchored Ion container. One
 * portal, one node tree, whose GEOMETRY (rail/docked/full) is driven purely
 * by chatStore.ionSurface. Mounts once in AppShell and replaces the old
 * ChatLauncher (FAB) + ChatPanel (slide-over) pair — there is no longer a
 * component that mounts/unmounts as the user opens or expands the chat, so
 * the conversation, scroll position, and stream state never remount.
 *
 * Geometry contract (see chatStore.js ionSurface + ionGating.js):
 *   rail   — slim right launcher strip (old FAB). Header/messages/input stay
 *            in the DOM (so the conversation survives) but are visually
 *            collapsed via CSS keyed off [data-ion-surface="rail"].
 *   docked — right-anchored side-by-side column (old slide-over; de-modalized
 *            in WS 6a — no scrim/body-scroll-lock, content reflows beside it,
 *            see ion.css) at ≥1200px. In the 769–1199px gap (WS 6b, too
 *            narrow to also reserve a side-by-side column) it falls back to
 *            a scrimmed overlay with a body-scroll-lock instead — see the
 *            effect below. Header shows minimize/close/expand.
 *   full   — chat (+ a conversations rail on the left, + the artifact canvas on
 *            the right DURING A GUIDED SELECTION ONLY — see B3 and
 *            `canvasSurfaceActive`). Header shows collapse.
 *
 * Composition:
 *   ChatErrorBoundary
 *     ChatPanelHeader (controls vary by geometry)
 *     IonDemoBanner (only with the `?ion_demo=` sales flag)
 *     IonUsageMeter
 *     ChatMessageList (or ChatEmptyState when empty; IonHome instead when
 *       empty AND `full` — the Ion-Home landing, Task 8) + ChatInput
 *     IonSidebar (full only — recents + "New chat")
 *     ArtifactCanvas (full only, once a conversation exists, and only while a
 *       guided cell selection is on screen. Outside a flow it duplicated what
 *       the chat already showed; on an empty landing it spent half the width on
 *       "nothing here yet")
 *
 * Gating: the whole surface (including the rail affordance) is hidden via
 * isIonVisible() — same rule the old ChatLauncher/ChatPanel each duplicated
 * (admin portal, access not yet resolved, env/user-level kill-switch).
 *
 * All hooks run on every render regardless of visibility/geometry — the
 * component itself never conditionally unmounts, so gating and per-geometry
 * behavior are applied INSIDE each effect/branch, never via an early return
 * before a hook (that would violate the rules of hooks now that this is one
 * persistent instance instead of a component that mounted/unmounted per route).
 */

import { createPortal } from 'preact/compat'
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { effect } from '@preact/signals'
import {
  ionSurface,
  tierProfile,
  chatMessages,
  canvasSurfaceActive,
  needsConversationRehydrate,
  ionIdentityToken,
  chatAccess,
  ionHidden,
  chatMinimized,
  chatHasUnseen,
  chatStreaming,
  currentConversationId,
  autoSendDraft,
  canSend,
  draftInput,
  openChat,
  closeChat,
  minimizeChat,
  restoreChat,
  clearConversation,
  expandToFullpage,
  collapseFromFull,
  openFeedback,
} from '../../state/chatStore.js'
import { togglePacingEditor } from '../../state/pacingEditorState.js'
import { canGiveIonFeedback } from '../../state/userState.js'
import { isIonVisible } from './ionGating.js'
import { activeTour, currentStep } from '../../tours/tourState.js'
import { TOURS, ION_INTRO_HANDOVER_PROMPTS } from '../../tours/tourDefinitions.js'
import { IonRailButton } from './IonRailButton.jsx'
import { IonToast } from './IonToast.jsx'
import { ChatPanelHeader } from './ChatPanelHeader.jsx'
import { ChatMessageList } from './ChatMessageList.jsx'
import { ChatInput } from './ChatInput.jsx'
import { ChatEmptyState } from './ChatEmptyState.jsx'
import { IonIntroHandoverChips } from './IonIntroHandoverChips.jsx'
import { ChatErrorBoundary } from './ChatErrorBoundary.jsx'
import { ConversationHistoryDrawer } from './ConversationHistoryDrawer.jsx'
import { IonCapabilityGallery } from './IonCapabilityGallery.jsx'
import IonHome from './IonHome.jsx'
import { IonSidebar } from './IonSidebar.jsx'
import { FeedbackModal } from './FeedbackModal.jsx'
import { IonUsageMeter } from './IonUsageMeter.jsx'
import { IonDemoBanner } from './IonDemoBanner.jsx'
import { ArtifactCanvas } from './canvas/ArtifactCanvas.jsx'
import { loadConversationIntoStore } from '../../services/ionConversationService.js'
import { sendChatMessage } from '../../hooks/useChatStream.js'

/**
 * @param {object} props
 * @param {string} [props.userName] — passed through to empty state
 * @param {object} [props.context] — page-context for the empty state
 * @param {object} [props.contextHint] — passed to ChatInput / ChatEmptyState send actions
 */
export function IonSurface({ userName, context, contextHint }) {
  const surface = ionSurface.value
  const open = surface !== 'rail' // docked or full — mirrors the old chatOpen
  // Whether the right-hand artifact column renders (B3). Computed once because
  // the grid class and the column itself MUST agree: a --solo grid with a canvas
  // in it clips the canvas, and a two-column grid without one leaves a 60fr hole.
  const canvasVisible =
    surface === 'full' && chatMessages.value.length > 0 && canvasSurfaceActive.value
  // Pacing is self-service for PRO/batemo only — DEMO caps are fixed and the
  // backend rejects a DEMO write with 403 `pacing_not_editable`
  // (chat/router.py::set_my_pacing), so those users get no button at all rather
  // than one that fails when pressed.
  const canEditPacing = tierProfile.value === 'pro' || tierProfile.value === 'batemo'
  const minimized = chatMinimized.value
  const hasUnseen = chatHasUnseen.value
  const [location] = useLocation()
  const surfaceRef = useRef(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const visible = isIonVisible({
    pathname: location,
    access: chatAccess.value,
    ionHidden: ionHidden.value,
  })

  // Fix A (S4 fix-wave regression): is the ion-intro tour's `intro-handover`
  // step the one currently showing? Drives the footer chip strip below —
  // see IonIntroHandoverChips.jsx's doc comment for the full "why".
  const introHandoverActive =
    activeTour.value === 'ion-intro' &&
    TOURS['ion-intro'].steps[currentStep.value]?.id === 'intro-handover'

  // When the surface opens (docked or full) with a known conversation whose
  // messages aren't (still) ours, pull them from the backend so a returning
  // user sees their OWN history instead of an empty thread — or, worse, the
  // previous account's.
  //
  // The condition is IDENTITY-based, not emptiness-based (see
  // chatStore.needsConversationRehydrate). The old form skipped the fetch
  // whenever `chatMessages` was non-empty, so after an account switch in the
  // same tab the previous account's residue suppressed the authoritative
  // user-scoped fetch and a foreign conversation id survived — which the
  // backend then answered with a 500 (prod, 2026-07-23..28). Identity is also
  // in the dep array so the switch itself re-runs this, not only a
  // visible/open transition.
  const identity = ionIdentityToken()
  useEffect(() => {
    if (!visible || !open) return
    if (!needsConversationRehydrate()) return
    loadConversationIntoStore(currentConversationId.peek()).catch(() => {
      /* failure is logged inside the service; UI stays in the empty state.
         A 404/403 additionally drops the stale id + messages there, so the
         foreign UUID is never re-sent on the next POST /chat/stream. */
    })
  }, [visible, open, identity])

  // Autosend: when the guided cell-selection entry seeds a prompt
  // (autoSendDraft — see CellSelectionEntry's gotoAssistant, the sole
  // producer), fire it exactly once per draft as soon as the stream/auth is
  // ready. Ported from the old fullpage AssistantPage (deleted in Task 4);
  // this was previously consumed on mount by a page that remounted per
  // navigation to /assistant, so a per-mount boolean guard was enough — each
  // guided entry got a fresh mount and thus a fresh guard. Now that
  // IonSurface mounts once and never remounts, a boolean guard blocks EVERY
  // draft after the first ever fires, across any number of later guided
  // entries. Track the last-CONSUMED draft object instead of a boolean, so a
  // new draft (a new object from gotoAssistant) always fires while the same
  // object re-triggering the effect (e.g. an unrelated re-render) does not.
  // Also closing over `contextHint` directly in a []-effect would go stale
  // after the first render, so the fallback hint is read off a ref kept
  // current every render instead.
  // Guards preserved as-is: chatAccess.loading, chatStreaming, and canSend
  // (quota / empty-draft), plus clearing the signal the instant we fire so
  // it can never re-fire.
  // NOTE (unscoped edge case): a draft that keeps failing every guard (e.g.
  // quota stays exhausted) sits in `autoSendDraft` with no notion of "the
  // page it was meant for" — since IonSurface is a single global instance,
  // it will still fire on whatever page the user has since navigated to the
  // moment every guard finally clears, silently overwriting that page's own
  // draftInput. Not scoping this to the /assistant route because the effect
  // closure would need a reactive (ref-refreshed, same staleness problem as
  // contextHintRef) read of the current pathname, and the guards above
  // normally clear within the same tick the producer navigates — a stuck
  // guard is the rare case. Left as-is; revisit if this bites in practice.
  const contextHintRef = useRef(contextHint)
  contextHintRef.current = contextHint
  const consumedDraftRef = useRef(null)
  useEffect(() => {
    const dispose = effect(() => {
      const pending = autoSendDraft.value
      if (!pending || pending === consumedDraftRef.current) return
      if (chatAccess.value.loading) return // access/auth not resolved yet
      if (chatStreaming.value) return // a stream is already live
      if (!canSend.value) return // quota / empty-draft guard
      consumedDraftRef.current = pending
      const hint = pending.contextHint ?? contextHintRef.current
      autoSendDraft.value = null
      draftInput.value = ''
      sendChatMessage(pending.text, {
        contextHint: hint,
        // S4: an onboarding draft (the ion-intro hero turn) carries its own
        // flags; ordinary guided-selection drafts have neither, so this is a
        // no-op for them.
        onboarding: pending.onboarding === true,
        example: pending.example === true,
      })
    })
    return dispose
  }, [])

  // Esc MINIMIZES the docked panel (non-destructive — keeps the conversation
  // and shows the rail's active indicator). Only wired for `docked` — the old
  // slide-over's behavior; the old fullpage AssistantPage never had an Esc
  // handler, so `full` keeps that same absence. Skipped while streaming —
  // ChatInput handles Esc-to-stop there.
  useEffect(() => {
    if (!visible || surface !== 'docked') return
    function onKey(e) {
      if (e.key === 'Escape' && !chatStreaming.peek()) {
        minimizeChat()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, surface])

  // Lock body scroll while `full` (a genuine full-screen split, unchanged),
  // OR while `docked` in the 769–1199px OVERLAY range (WS 6b: in that range
  // docked is a scrimmed, click-outside-closes modal — see ion.css's
  // de-modalize gate, which only turns pointer-events/the scrim back OFF at
  // ≥1200px side-by-side or ≤768px bottom-sheet). Neither of those two
  // ranges locks: ≥1200px docked is a true side-by-side column (the page
  // must stay scrollable/usable beside it — WS 6a) and ≤768px docked has
  // always been a non-locking bottom-sheet. Tracked live via matchMedia so
  // resizing across the breakpoint while docked stays open (re)evaluates
  // the lock instead of freezing whatever width was true when the effect
  // first ran.
  useEffect(() => {
    if (!visible || typeof document === 'undefined') return
    if (surface !== 'full' && surface !== 'docked') return

    // matchMedia is only needed to detect the docked overlay range — `full`
    // must keep locking regardless (matches the old, matchMedia-free
    // behavior), so its absence (e.g. this repo's jsdom test environment,
    // which doesn't implement it — see RankedCellsTable.jsx/SalesRagonePlot.jsx
    // for the same guard) only means docked never enters the locked branch,
    // never that `full` stops locking.
    const hasMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    const overlayRange = hasMatchMedia
      ? window.matchMedia('(min-width: 769px) and (max-width: 1199px)')
      : null
    const prev = document.body.style.overflow

    function sync() {
      const shouldLock = surface === 'full' || Boolean(overlayRange?.matches)
      document.body.style.overflow = shouldLock ? 'hidden' : prev
    }

    sync()
    overlayRange?.addEventListener('change', sync)
    return () => {
      overlayRange?.removeEventListener('change', sync)
      document.body.style.overflow = prev
    }
  }, [visible, surface])

  // The docked entrance is a clip-path circle that grows from the rail to the
  // panel's farthest corner. CSS can't compute that radius (no sqrt), and an
  // over-large fallback makes the visible reveal finish at ~40% of the duration
  // (looks too fast). Compute the exact radius via Math.hypot and feed it to the
  // CSS as --ion-reveal-r. Depends only on the panel's box, so (re)compute on
  // mount and on resize — not per open. Ported unchanged from ChatPanel; now
  // runs once for the surface's whole lifetime since IonSurface never remounts.
  useLayoutEffect(() => {
    const el = surfaceRef.current
    if (!el) return undefined
    const FAB_OFFSET = 28 // must match --ion-launcher-offset
    const FAB_SIZE = 56 // must match --ion-launcher-size
    const PANEL_INSET = 8 // must match --ion-panel-inset
    function setRevealRadius() {
      const { width, height } = el.getBoundingClientRect()
      if (!width || !height) return
      // FAB centre relative to the surface's top-left. The surface is inset from
      // the viewport's bottom-right by PANEL_INSET, so the FAB centre sits
      // (FAB_OFFSET + FAB_SIZE/2 − PANEL_INSET) in from the surface's edge —
      // mirroring --ion-fab-origin-x/y in ion.css.
      const ox = width - FAB_OFFSET - FAB_SIZE / 2 + PANEL_INSET
      const oy = height - FAB_OFFSET - FAB_SIZE / 2 + PANEL_INSET
      const r = Math.hypot(Math.max(ox, width - ox), Math.max(oy, height - oy))
      el.style.setProperty('--ion-reveal-r', `${Math.ceil(r)}px`)
    }
    setRevealRadius()
    window.addEventListener('resize', setRevealRadius)
    return () => window.removeEventListener('resize', setRevealRadius)
  }, [])

  // Rail-heartbeat (S3, .planning/ion-onboarding-s3-s4-design.md): the
  // collapsed rail shows Ion is present + working instead of reading as a
  // dead button. Split out into IonRailButton (fix-wave review, Fix 3) so
  // its signal reads (chatStreaming, commandProvenance, filteredCells,
  // allCells) and the steering-decay interval only re-render that leaf —
  // IonSurface itself is a single always-mounted instance for the app's
  // whole lifetime, so reading those signals here would re-render the
  // entire Ion subtree (header, usage meter, message list, input, history
  // drawer) on every filter/slider tick on /explorer.
  if (!visible) return null
  if (typeof document === 'undefined') return null

  function handleRailClick() {
    // A minimized chat re-opens the SAME conversation; otherwise open fresh.
    if (minimized) restoreChat()
    else openChat()
  }

  return createPortal(
    <div
      class="ion-panel"
      data-ion-surface={surface}
      // `rail` is just a launcher button, not a dialog at all — no role.
      // `docked` (WS 6a) is now a true side-by-side column, not modal: no
      // scrim, no outside-click-to-close, no body-scroll-lock (see the
      // effect above and ion.css's de-modalize block) at ≥1200px — though
      // 769–1199px (WS 6b) does scrim + lock scroll, the role stays the
      // same at every width — so it does NOT get aria-modal. It still needs
      // a landmark role so assistive tech can
      // jump straight to it as a distinct region; role="complementary" is
      // the ARIA role for supporting content that lives alongside — and is
      // usable independently of — the main content, which is exactly what a
      // permanent side-by-side assistant column is (as opposed to
      // role="dialog", which implies a discrete task the user opened and
      // will dismiss). `full` still covers the viewport below the header as
      // a genuine full-screen split with a locked background, so it keeps
      // role="dialog" + aria-modal="true", matching the old fullpage
      // AssistantPage / ChatPanel slide-over's semantics.
      role={surface === 'rail' ? undefined : surface === 'docked' ? 'complementary' : 'dialog'}
      aria-modal={surface === 'full' ? 'true' : undefined}
      aria-label="Batemo Ion assistant"
    >
      <IonRailButton
        minimized={minimized}
        surface={surface}
        pathname={location}
        hasUnseen={hasUnseen}
        onClick={handleRailClick}
      />
      <IonToast />

      <div class="ion-panel__scrim" onClick={closeChat} />
      {/* aria-hidden lives here (not on the .ion-panel root) so the rail
          button and IonToast above — genuine siblings, not descendants of
          this wrap — stay reachable to screen readers while the surface
          content is collapsed in `rail`. */}
      <div class="ion-panel__surface-wrap" aria-hidden={surface === 'rail'}>
        <aside class="ion-panel__surface" ref={surfaceRef}>
          <ChatErrorBoundary>
            <ChatPanelHeader
              surface={surface}
              onNewConversation={clearConversation}
              onExpand={surface === 'docked' ? expandToFullpage : undefined}
              onCollapse={surface === 'full' ? collapseFromFull : undefined}
              onHistory={() => setHistoryOpen(true)}
              onPacing={canEditPacing ? togglePacingEditor : undefined}
              onFeedback={
                canGiveIonFeedback() && currentConversationId.value
                  ? () => openFeedback({ scope: 'conversation' })
                  : undefined
              }
              onMinimize={surface === 'docked' ? minimizeChat : undefined}
              onClose={surface === 'docked' ? closeChat : undefined}
            />
            {/* Ion's own chat-request quota meter (WS 2.2) — self-guards to its
                tier and renders 0-height otherwise.

                DemoUsageMeter (the deep-dive counter) deliberately does NOT
                live here. It was ported in from the old fullpage AssistantPage
                "so `full` keeps demo-quota visibility", but the CommandBar
                header renders it on every in-app page including /assistant —
                so a demo user saw the same "X of N deep dives" twice at once,
                once in the header and once here (BA, 2026-08-05). The header is
                the canonical mount: it is the one that is always visible, and
                the count belongs to the whole app rather than to Ion. */}
            {/* Sales-demo marker — self-guards to null unless `?ion_demo=` is
                set, so it costs nothing in normal use. It had lost its mount
                when ChatPanel was replaced by this component. */}
            <IonDemoBanner />
            <div class="ion-panel__usage-meter">
              <IonUsageMeter />
            </div>
            {/* --solo when the canvas column is absent (see below), so the grid
                drops to one column and Ion-Home may use a wider measure than
                the 560px that suits a narrow chat column. */}
            {/* One source of truth for "is the canvas column there", used by both
                the column itself below and the --solo grid class here. Before
                B3 the two could not disagree (the canvas rendered whenever the
                conversation was non-empty), so --solo keyed off emptiness. Now
                the canvas is also absent outside a flow, and keying --solo off
                emptiness would leave a 60fr track holding nothing — which is the
                same half-empty layout the --solo class was introduced to fix. */}
            <div
              class={`ion-panel__split${
                canvasVisible ? '' : ' ion-panel__split--solo'
              }${surface === 'full' ? ' ion-panel__split--sidebar' : ''}`}
            >
              {/* A2: recents + "New chat", full page only. The docked panel is
                  370px of right-edge space beside the user's real work — a rail
                  in there would be taking room from the conversation itself. */}
              {surface === 'full' && <IonSidebar />}
              {/* data-tour anchor for ion-intro's `intro-handover` step (C1) —
                  spans both the ChatEmptyState prompt chips (in __body) and
                  the composer (__footer), so the interactive cutout covers
                  everywhere the user can act on the handover. */}
              <div class="ion-panel__chat" data-tour="ion-intro-handover">
                <div class="ion-panel__body">
                  {surface === 'full' && chatMessages.value.length === 0 ? (
                    // Ion-Home landing (Task 8) — full geometry, no
                    // conversation yet. Swaps in for ChatMessageList only;
                    // the footer ChatInput below and the canvas aside stay
                    // exactly as they are.
                    <IonHome />
                  ) : (
                    <ChatMessageList
                      empty={
                        <ChatEmptyState
                          userName={userName}
                          context={context}
                          contextHint={contextHint}
                          prompts={
                            activeTour.value === 'ion-intro'
                              ? ION_INTRO_HANDOVER_PROMPTS
                              : undefined
                          }
                        />
                      }
                    />
                  )}
                </div>
                <div class="ion-panel__footer">
                  {/* Fix A: only when the conversation is NON-EMPTY —
                      ChatEmptyState (rendered via ChatMessageList's `empty`
                      slot above) already shows these same prompts when it
                      IS empty, so exactly one of the two ever renders. */}
                  {introHandoverActive && chatMessages.value.length > 0 && (
                    <IonIntroHandoverChips
                      prompts={ION_INTRO_HANDOVER_PROMPTS}
                      contextHint={contextHint}
                    />
                  )}
                  <ChatInput contextHint={contextHint} autoFocus={open} />
                </div>
              </div>
              {/* The canvas column appears only during a guided cell selection
                  (B3, `canvasSurfaceActive` — see chatStore for why that is
                  three conditions and not one).
                  Outside a flow it showed a COPY of something the chat already
                  contained, which is the review's "the plot is rendered twice",
                  over a tab strip belonging to a flow that was not running.
                  It is also still gated on a non-empty conversation: while the
                  conversation is EMPTY the column used to render anyway, filling
                  half the landing page with "Nothing to work on yet" beside
                  Ion-Home's narrow column — an empty state should not spend half
                  its area explaining that it is empty. */}
              {canvasVisible && (
                <aside class="ion-panel__canvas" aria-label="Artifact canvas">
                  <ArtifactCanvas />
                </aside>
              )}
            </div>
            <ConversationHistoryDrawer
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
            />
            {/* Reads its own open state (capabilityGalleryOpen) rather than a
                prop — see IonCapabilityGallery.jsx's doc comment for why: it
                has entry points outside IonSurface's tree (HelpMenuButton). */}
            <IonCapabilityGallery />
            <FeedbackModal />
          </ChatErrorBoundary>
        </aside>
      </div>
    </div>,
    document.body,
  )
}
